import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Loader2 } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { getBlog } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlog().then(setPosts).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <PageHero
        breadcrumb="Blog"
        eyebrow="Industry Insights"
        title="Moving smarter, together."
        description="Expert tips, packing guides and logistics insights from our team of relocation specialists."
      />

      <section className="bg-white py-24 lg:py-32">
        <div className="container-x">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
          ) : (
            <>
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  className="group grid lg:grid-cols-2 gap-8 rounded-3xl overflow-hidden border border-gray-100 shadow-soft hover:shadow-card transition-shadow duration-300 mb-14"
                  data-testid="blog-featured"
                >
                  <div className="relative h-72 lg:h-full overflow-hidden">
                    <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="rounded-full bg-brand-light text-brand px-3 py-1 font-semibold">{featured.category}</span>
                      <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-4 w-4" />{featured.read_time}</span>
                    </div>
                    <h2 className="mt-5 font-heading font-extrabold text-3xl sm:text-4xl text-ink tracking-tight leading-tight group-hover:text-brand transition-colors">
                      {featured.title}
                    </h2>
                    <p className="mt-4 text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-semibold text-brand">
                      Read Article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </div>
                </Link>
              )}

              <StaggerGroup className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((p) => (
                  <StaggerItem key={p.slug} className="h-full">
                    <Link to={`/blog/${p.slug}`} className="group flex h-full flex-col rounded-3xl overflow-hidden border border-gray-100 shadow-soft hover:shadow-card transition-all duration-300 hover:-translate-y-1.5" data-testid={`blog-card-${p.slug}`}>
                      <div className="relative h-52 overflow-hidden">
                        <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <div className="flex items-center gap-3 text-xs">
                          <span className="rounded-full bg-brand-light text-brand px-3 py-1 font-semibold">{p.category}</span>
                          <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{p.read_time}</span>
                        </div>
                        <h3 className="mt-4 font-heading font-bold text-xl text-ink group-hover:text-brand transition-colors leading-snug">{p.title}</h3>
                        <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
                        <span className="mt-5 text-sm font-semibold text-brand inline-flex items-center gap-1">Read more <ArrowUpRight className="h-4 w-4" /></span>
                      </div>
                    </Link>
                  </StaggerItem>
                ))}
              </StaggerGroup>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
